import { faLock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getPreparationStatusColor } from '@modules/OrderPreparation/shared/config';
import { Badge } from '@shared/components/ui/badge';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DropdownActions } from '../components/interaction/DropdownActions';
import { Cart, RelatedObject } from '../models/Cart';

// 🎯 Tipos para los handlers
interface UseAgGridColumnsProps {
  onCartEdit?: (cart: Cart) => void;
  onCartDelete?: (cart: Cart) => void;
  onCartAdjust?: (cart: Cart) => void;
  onCartPrint?: (cart: Cart, size: 'small' | 'large') => void;
  onCartToggleLock?: (cart: Cart) => void;
  onCartViewProducts?: (cart: Cart) => void;
}

// 🎯 Hook para manejar las columnas de AG Grid
export const useAgGridColumns = ({
  onCartEdit,
  onCartDelete,
  onCartAdjust,
  onCartPrint,
  onCartToggleLock,
  onCartViewProducts,
}: UseAgGridColumnsProps) => {
  const { t } = useTranslation('cartsModule');

  // 🎯 Función para obtener el nombre del estado de preparación
  const getPreparationStatusName = (status: number): string => {
    const statusMap: Record<number, string> = {
      0: t('status.preparation.picking'),
      1: t('status.preparation.picking'),
      2: t('status.preparation.packing'),
      3: t('status.preparation.completed'),
      4: t('status.preparation.canceled'),
    };
    return statusMap[status] || t('status.preparation.unknown');
  };

  // 🎯 Función para obtener el color del badge según el estado de location_order
  const getLocationOrderStatusColor = (status: string | number): string => {
    const statusStr = String(status).toLowerCase();
    switch (statusStr) {
      case 'done':
      case 'completo':
        return 'text-success light:bg-bg-success';
      case 'pending':
      case 'pendiente':
        return 'text-badge-status-3 light:bg-badge-bg-status-3';
      case 'in_progress':
      case 'en_proceso':
        return 'text-warning light:bg-bg-warning';
      case 'cancelled':
      case 'cancelado':
        return 'text-error light:bg-bg-error';
      default:
        return 'text-badge-status-3';
    }
  };

  // 🎯 Función para obtener el nombre del estado de location_order
  const getLocationOrderStatusName = (status: string | number): string => {
    const statusStr = String(status).toLowerCase();
    switch (statusStr) {
      case 'done':
        return t('status.location_order.done');
      case 'pending':
        return t('status.location_order.pending');
      case 'in_progress':
        return t('status.location_order.in_progress');
      case 'cancelled':
        return t('status.location_order.cancelled');
      default:
        return t('status.location_order.unknown');
    }
  };

  // 🎯 Función para obtener el color del badge según el estado de returned_reception
  const getReturnedReceptionStatusColor = (status: number): string => {
    switch (status) {
      case 0: // CLOSED
        return 'text-muted-foreground light:bg-gray-100';
      case 1: // OPEN
        return 'text-success light:bg-bg-success';
      case 2: // POR_UBICAR
        return 'text-warning light:bg-bg-warning';
      case 3: // PENDING_OPEN
        return 'text-warning light:bg-bg-warning';
      default:
        return 'text-badge-status-3';
    }
  };

  // 🎯 Función para obtener el nombre del estado de returned_reception
  const getReturnedReceptionStatusName = (status: number): string => {
    switch (status) {
      case 0:
        return t('status.returned_reception.closed');
      case 1:
        return t('status.returned_reception.open');
      case 2:
        return t('status.returned_reception.por_ubicar');
      case 3:
        return t('status.returned_reception.pending_open');
      default:
        return t('status.returned_reception.unknown');
    }
  };

  // 🎯 Función para obtener el color del badge según el estado de merchandise
  const getMerchandiseStatusColor = (status: number): string => {
    switch (status) {
      case 0: // CANCELED
        return 'text-error light:bg-bg-error';
      case 1: // PENDING
        return 'text-badge-status-1 light:bg-badge-bg-status-1';
      case 2: // PARTIAL_RECEIVED
        return 'text-warning light:bg-bg-warning';
      case 3: // RECEIVED
        return 'text-success light:bg-bg-success';
      case 4: // CLOSED
        return 'text-muted-foreground light:bg-gray-100';
      default:
        return 'text-badge-status-3';
    }
  };

  // 🎯 Función para obtener el nombre del estado de merchandise
  const getMerchandiseStatusName = (status: number): string => {
    switch (status) {
      case 0:
        return t('status.merchandise.canceled');
      case 1:
        return t('status.merchandise.pending');
      case 2:
        return t('status.merchandise.partial_received');
      case 3:
        return t('status.merchandise.received');
      case 4:
        return t('status.merchandise.closed');
      default:
        return t('status.merchandise.unknown');
    }
  };

  // 🎯 Función para obtener el color del badge según el estado de extra_work_order
  const getExtraWorkOrderStatusColor = (status: string): string => {
    switch (status?.toUpperCase()) {
      case 'PENDING':
        return 'text-badge-status-1 light:bg-badge-bg-status-1';
      case 'IN_PROGRESS':
        return 'text-warning light:bg-bg-warning';
      case 'DONE':
        return 'text-success light:bg-bg-success';
      default:
        return 'text-badge-status-3';
    }
  };

  // 🎯 Función para obtener el nombre del estado de extra_work_order
  const getExtraWorkOrderStatusName = (status: string): string => {
    switch (status?.toUpperCase()) {
      case 'PENDING':
        return t('status.extra_work_order.pending');
      case 'IN_PROGRESS':
        return t('status.extra_work_order.in_progress');
      case 'DONE':
        return t('status.extra_work_order.done');
      default:
        return t('status.extra_work_order.unknown');
    }
  };

  // 🎯 Función para traducir status_label de location_order
  const translateLocationOrderStatusLabel = (
    statusLabel: string | undefined,
    status: string | number | undefined,
  ): string => {
    if (!statusLabel && status !== undefined) {
      return getLocationOrderStatusName(status);
    }
    if (!statusLabel) return t('status.location_order.unknown');

    const labelUpper = statusLabel.toUpperCase();
    switch (labelUpper) {
      case 'PENDING':
        return t('status.location_order.pending');
      case 'IN_PROGRESS':
        return t('status.location_order.in_progress');
      case 'DONE':
        return t('status.location_order.done');
      case 'CANCELLED':
        return t('status.location_order.cancelled');
      default:
        return status ? getLocationOrderStatusName(status) : statusLabel;
    }
  };

  // 🎯 Función para traducir status_label de preparation
  const translatePreparationStatusLabel = (statusLabel: string | undefined, status: number | undefined): string => {
    if (!statusLabel && status !== undefined) {
      return getPreparationStatusName(status);
    }
    if (!statusLabel) return t('status.preparation.unknown');

    const labelUpper = statusLabel.toUpperCase();
    switch (labelUpper) {
      case 'CANCELED':
      case 'CANCELLED':
        return t('status.preparation.canceled');
      case 'PENDING':
        return t('status.preparation.picking');
      case 'PICKED':
        return t('status.preparation.packing');
      case 'COMPLETED':
        return t('status.preparation.completed');
      default:
        return status !== undefined ? getPreparationStatusName(status) : statusLabel;
    }
  };

  // 🎯 Función para traducir status_label de returned_reception
  const translateReturnedReceptionStatusLabel = (
    statusLabel: string | undefined,
    status: number | undefined,
  ): string => {
    if (!statusLabel && status !== undefined) {
      return getReturnedReceptionStatusName(status);
    }
    if (!statusLabel) return t('status.returned_reception.unknown');

    const labelUpper = statusLabel.toUpperCase();
    switch (labelUpper) {
      case 'CLOSED':
        return t('status.returned_reception.closed');
      case 'OPEN':
        return t('status.returned_reception.open');
      case 'POR_UBICAR':
        return t('status.returned_reception.por_ubicar');
      default:
        return status !== undefined ? getReturnedReceptionStatusName(status) : statusLabel;
    }
  };

  // 🎯 Función para traducir status_label de merchandise
  const translateMerchandiseStatusLabel = (statusLabel: string | undefined, status: number | undefined): string => {
    if (!statusLabel && status !== undefined) {
      return getMerchandiseStatusName(status);
    }
    if (!statusLabel) return t('status.merchandise.unknown');

    const labelUpper = statusLabel.toUpperCase();
    switch (labelUpper) {
      case 'CANCELED':
      case 'CANCELLED':
        return t('status.merchandise.canceled');
      case 'PENDING':
        return t('status.merchandise.pending');
      case 'PARTIAL_RECEIVED':
        return t('status.merchandise.partial_received');
      case 'RECEIVED':
        return t('status.merchandise.received');
      case 'CLOSED':
        return t('status.merchandise.closed');
      default:
        return status !== undefined ? getMerchandiseStatusName(status) : statusLabel;
    }
  };

  // 🎯 Función para traducir status_label de extra_work_order
  const translateExtraWorkOrderStatusLabel = (statusLabel: string | undefined, status: string | undefined): string => {
    if (!statusLabel && status) {
      return getExtraWorkOrderStatusName(status);
    }
    if (!statusLabel) return t('status.extra_work_order.unknown');

    const labelUpper = statusLabel.toUpperCase();
    switch (labelUpper) {
      case 'PENDING':
        return t('status.extra_work_order.pending');
      case 'IN_PROGRESS':
        return t('status.extra_work_order.in_progress');
      case 'DONE':
        return t('status.extra_work_order.done');
      default:
        return status ? getExtraWorkOrderStatusName(status) : statusLabel;
    }
  };

  // 🎯 Función para obtener el color del badge según el estado de entry
  const getEntryStatusColor = (status: string): string => {
    const statusStr = String(status).toUpperCase();
    switch (statusStr) {
      case 'PENDIENTE':
      case 'PENDING':
        return 'text-badge-status-3 light:bg-badge-bg-status-3';
      case 'EN_RECEPCION':
      case 'IN_RECEPTION':
        return 'text-warning light:bg-bg-warning';
      case 'EN_RECEPCION_PARCIAL':
      case 'IN_RECEPTION_PARTIAL':
        return 'text-warning light:bg-bg-warning';
      case 'FINALIZADA':
      case 'FINALIZED':
        return 'text-success light:bg-bg-success';
      case 'SIN_IDENTIFICAR':
      case 'UNIDENTIFIED':
        return 'text-badge-status-3';
      case 'CANCELADA':
      case 'CANCELLED':
        return 'text-error light:bg-bg-error';
      default:
        return 'text-badge-status-3';
    }
  };

  // 🎯 Función para obtener el nombre del estado de entry
  const getEntryStatusName = (status: string): string => {
    const statusStr = String(status).toUpperCase();
    switch (statusStr) {
      case 'PENDIENTE':
      case 'PENDING':
        return t('status.entry.pending');
      case 'EN_RECEPCION':
      case 'IN_RECEPTION':
        return t('status.entry.in_reception');
      case 'EN_RECEPCION_PARCIAL':
      case 'IN_RECEPTION_PARTIAL':
        return t('status.entry.in_reception_partial');
      case 'FINALIZADA':
      case 'FINALIZED':
        return t('status.entry.finalized');
      case 'SIN_IDENTIFICAR':
      case 'UNIDENTIFIED':
        return t('status.entry.unidentified');
      case 'CANCELADA':
      case 'CANCELLED':
        return t('status.entry.cancelled');
      default:
        return t('status.entry.unknown');
    }
  };

  // 🎯 Función para traducir status_label de entry
  const translateEntryStatusLabel = (statusLabel: string | undefined, status: string | undefined): string => {
    if (!statusLabel && status) {
      return getEntryStatusName(status);
    }
    if (!statusLabel) return t('status.entry.unknown');

    const labelUpper = statusLabel.toUpperCase();
    switch (labelUpper) {
      case 'PENDIENTE':
      case 'PENDING':
        return t('status.entry.pending');
      case 'EN_RECEPCION':
      case 'IN_RECEPTION':
        return t('status.entry.in_reception');
      case 'EN_RECEPCION_PARCIAL':
      case 'IN_RECEPTION_PARTIAL':
        return t('status.entry.in_reception_partial');
      case 'FINALIZADA':
      case 'FINALIZED':
        return t('status.entry.finalized');
      case 'SIN_IDENTIFICAR':
      case 'UNIDENTIFIED':
        return t('status.entry.unidentified');
      case 'CANCELADA':
      case 'CANCELLED':
        return t('status.entry.cancelled');
      default:
        return status ? getEntryStatusName(status) : statusLabel;
    }
  };

  // 🎯 Definiciones completas de columnas para AG Grid
  const allColumnDefs: ColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      // 🔵 COLUMNA: Código
      {
        field: 'code',
        headerName: t('table.code'),
        minWidth: 150,
        sortable: false,
        // 🎯 CELDA: Código con ícono de candado si está bloqueado
        cellRenderer: (params: any) => {
          const cart = params.data as Cart;
          return (
            <div className="flex items-center gap-2">
              {cart.is_locked && (
                <FontAwesomeIcon icon={faLock} className="text-title text-sm" title={t('status.locked')} />
              )}
              <span className="text-sm font-medium text-title">{cart.code}</span>
            </div>
          );
        },
      },

      // 🔵 COLUMNA: Tarea (Origen)
      {
        field: 'origin',
        headerName: t('table.origin'),
        minWidth: 200,
        sortable: false, // Campo calculado, no sortable
        // 🎯 CELDA: Related Objects (location_order, preparation, etc.)
        cellRenderer: (params: any) => {
          const cart = params.data as Cart;
          const relatedObjects = cart.related_objects || [];

          // 🎯 Prioridad: mostrar related_objects si existen
          if (relatedObjects.length > 0) {
            return (
              <div className="flex flex-col gap-1">
                {relatedObjects.map((relatedObj: RelatedObject, index: number) => {
                  // CASO 1: Location Order
                  if (relatedObj.type === 'location_order') {
                    return (
                      <div key={index} className="flex flex-col gap-1">
                        <span className="text-xs text-title">{t('table.location_order', 'Orden de Ubicación')}</span>
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/location-orders/${relatedObj.id}`}
                            className="text-sm font-medium text-title hover:text-foreground/70 underline decoration-1 underline-offset-2 hover:decoration-2 transition-all"
                          >
                            #{relatedObj.id}
                          </Link>
                          {relatedObj.status !== undefined && (
                            <Badge
                              variant="outline"
                              className={`text-xs bg-card-border border-0 ${getLocationOrderStatusColor(relatedObj.status)}`}
                            >
                              {translateLocationOrderStatusLabel(relatedObj.status_label, relatedObj.status)}
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // CASO 2: Preparation
                  if (relatedObj.type === 'preparation') {
                    // Determinar la URL según el status (si está disponible)
                    const status =
                      typeof relatedObj.status === 'number'
                        ? relatedObj.status
                        : relatedObj.status
                          ? parseInt(String(relatedObj.status))
                          : undefined;
                    const url =
                      status !== undefined && (status === 0 || status === 1)
                        ? `/order-preparation/picking/${relatedObj.id}`
                        : `/order-preparation/packing/${relatedObj.id}`;

                    return (
                      <div key={index} className="flex flex-col gap-1">
                        <span className="text-xs text-title">{t('table.preparation')}</span>
                        <div className="flex items-center gap-2">
                          <Link
                            to={url}
                            className="text-sm font-medium text-title hover:text-foreground/70 underline decoration-1 underline-offset-2 hover:decoration-2 transition-all"
                          >
                            {relatedObj.code || `#${relatedObj.id}`}
                          </Link>
                          {relatedObj.status !== undefined && (
                            <Badge
                              variant="outline"
                              className={`text-xs bg-card-border border-0 ${
                                status !== undefined ? getPreparationStatusColor(status) : 'text-badge-status-3'
                              }`}
                            >
                              {translatePreparationStatusLabel(relatedObj.status_label, status)}
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // CASO 3: Returned Reception
                  if (relatedObj.type === 'returned_reception') {
                    const status =
                      typeof relatedObj.status === 'number'
                        ? relatedObj.status
                        : relatedObj.status
                          ? parseInt(String(relatedObj.status))
                          : undefined;

                    return (
                      <div key={index} className="flex flex-col gap-1">
                        <span className="text-xs text-title">{t('table.returned_reception', 'Rehusados')}</span>
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/reused/${relatedObj.id}/receive`}
                            className="text-sm font-medium text-title hover:text-foreground/70 underline decoration-1 underline-offset-2 hover:decoration-2 transition-all"
                          >
                            {relatedObj.code || `#${relatedObj.id}`}
                          </Link>
                          {status !== undefined && (
                            <Badge
                              variant="outline"
                              className={`text-xs bg-card-border border-0 ${getReturnedReceptionStatusColor(status)}`}
                            >
                              {translateReturnedReceptionStatusLabel(relatedObj.status_label, status)}
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // CASO 4: Merchandise
                  if (relatedObj.type === 'merchandise') {
                    const status =
                      typeof relatedObj.status === 'number'
                        ? relatedObj.status
                        : relatedObj.status
                          ? parseInt(String(relatedObj.status))
                          : undefined;

                    return (
                      <div key={index} className="flex flex-col gap-1">
                        <span className="text-xs text-title">{t('table.merchandise')}</span>
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/merchandise/view-entry/${relatedObj.id}`}
                            className="text-sm font-medium text-title hover:text-foreground/70 underline decoration-1 underline-offset-2 hover:decoration-2 transition-all"
                          >
                            {relatedObj.ref || `#${relatedObj.id}`}
                          </Link>
                          {status !== undefined && (
                            <Badge
                              variant="outline"
                              className={`text-xs bg-card-border border-0 ${getMerchandiseStatusColor(status)}`}
                            >
                              {translateMerchandiseStatusLabel(relatedObj.status_label, status)}
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // CASO 5: Extra Work Order
                  if (relatedObj.type === 'extra_work_order') {
                    const status =
                      typeof relatedObj.status === 'string'
                        ? relatedObj.status
                        : relatedObj.status
                          ? String(relatedObj.status)
                          : undefined;

                    return (
                      <div key={index} className="flex flex-col gap-1">
                        <span className="text-xs text-title">
                          {t('table.extra_work_order', 'Orden de Trabajo Extra')}
                        </span>
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/extra-work-orders/${relatedObj.id}`}
                            className="text-sm font-medium text-title hover:text-foreground/70 underline decoration-1 underline-offset-2 hover:decoration-2 transition-all"
                          >
                            {relatedObj.ref || `#${relatedObj.id}`}
                          </Link>
                          {status && (
                            <Badge
                              variant="outline"
                              className={`text-xs bg-card-border border-0 ${getExtraWorkOrderStatusColor(status)}`}
                            >
                              {translateExtraWorkOrderStatusLabel(relatedObj.status_label, status)}
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // CASO 6: Entry
                  if (relatedObj.type === 'entry') {
                    const status =
                      typeof relatedObj.status === 'string'
                        ? relatedObj.status
                        : relatedObj.status
                          ? String(relatedObj.status)
                          : undefined;

                    return (
                      <div key={index} className="flex flex-col gap-1">
                        <span className="text-xs text-title">Entrada</span>
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/merchandise/view-entry/${relatedObj.id}`}
                            className="text-sm font-medium text-title hover:text-foreground/70 underline decoration-1 underline-offset-2 hover:decoration-2 transition-all"
                          >
                            {relatedObj.reference || relatedObj.code || relatedObj.ref || `#${relatedObj.id}`}
                          </Link>
                          {status && (
                            <Badge
                              variant="outline"
                              className={`text-xs bg-card-border border-0 ${getEntryStatusColor(status)}`}
                            >
                              {translateEntryStatusLabel(relatedObj.status_label, status)}
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // CASO 7: Otros tipos
                  return (
                    <div key={index} className="flex flex-col gap-1">
                      <span className="text-xs text-title">{relatedObj.type}</span>
                      <span className="text-sm font-medium">
                        {relatedObj.code || relatedObj.ref || `#${relatedObj.id}`}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          }

          // 🎯 Fallback: Preparación legacy (preparation_code)
          const preparationCode = cart.preparation_code;
          const preparationId = cart.preparation_id;
          const preparationStatus = cart.preparation_status;
          const merchandiseCode = cart.merchandise_code;

          // CASO A: Preparación con link y badge
          if (preparationCode && preparationId !== undefined && preparationStatus !== undefined) {
            // Determinar la URL según el status
            const url =
              preparationStatus === 0 || preparationStatus === 1
                ? `/order-preparation/picking/${preparationId}`
                : `/order-preparation/packing/${preparationId}`;

            return (
              <div className="flex flex-col gap-1">
                <span className="text-xs text-title">{t('table.preparation')}</span>
                <div className="flex items-center gap-2">
                  <Link
                    to={url}
                    className="text-sm font-medium text-title hover:text-foreground/70 underline decoration-1 underline-offset-2 hover:decoration-2 transition-all"
                  >
                    {preparationCode}
                  </Link>
                  <Badge
                    variant="outline"
                    className={`text-xs bg-card-border border-0 ${getPreparationStatusColor(preparationStatus)}`}
                  >
                    {getPreparationStatusName(preparationStatus)}
                  </Badge>
                </div>
              </div>
            );
          }

          // CASO B: Mercancía sin link
          if (merchandiseCode) {
            return (
              <div className="flex flex-col">
                <span className="text-xs text-title">{t('table.merchandise')}</span>
                <span className="text-sm font-medium">{merchandiseCode}</span>
              </div>
            );
          }

          // CASO C: Sin origen
          return <span className="text-sm text-title">-</span>;
        },
      },

      // 🔵 COLUMNA: Productos (Stock)
      {
        field: 'stock',
        headerName: t('table.stock'),
        minWidth: 100,
        sortable: false, // Campo calculado, no sortable
        // 🎯 CELDA: Cantidad de productos (clickeable si tiene stock)
        cellRenderer: (params: any) => {
          const cart = params.data as Cart;
          const hasProducts = cart.stock_location_carts && cart.stock_location_carts.length > 0;
          const stock = cart.stock || 0;

          if (hasProducts && onCartViewProducts) {
            return (
              <button
                onClick={() => onCartViewProducts(cart)}
                className="text-sm font-medium text-title hover:text-foreground/70 underline decoration-1 underline-offset-2 hover:decoration-2 transition-all cursor-pointer"
                title={t('table.view_products')}
                data-testid="view-products-button"
              >
                {stock}
              </button>
            );
          }

          return <span className="text-sm">{stock}</span>;
        },
      },

      // 🔵 COLUMNA: Acciones (siempre presente)
      {
        headerName: '',
        field: 'actions',
        width: 60,
        minWidth: 60,
        maxWidth: 60,
        resizable: false,
        pinned: 'right',
        lockPosition: 'right', // Evita que el usuario mueva la columna
        suppressMovable: true, // Deshabilita el drag & drop para esta columna
        suppressSizeToFit: true,
        suppressAutoSize: true,
        // 🎯 CELDA: Dropdown de acciones
        cellRenderer: (params: any) => {
          const cart = params.data as Cart;
          return (
            <div className="flex justify-end w-full">
              <DropdownActions
                cart={cart}
                onEdit={onCartEdit}
                onDelete={onCartDelete}
                onAdjust={onCartAdjust}
                onPrint={onCartPrint}
                onToggleLock={onCartToggleLock}
                onViewProducts={onCartViewProducts}
              />
            </div>
          );
        },
      },
    ];

    return baseColumns;
  }, [t, onCartEdit, onCartDelete, onCartAdjust, onCartPrint, onCartToggleLock, onCartViewProducts]);

  // 🎯 Para carritos no necesitamos ColumnSelector, retornamos las columnas directamente
  const filteredColumnDefs: ColDef[] = allColumnDefs;

  return {
    allColumnDefs,
    filteredColumnDefs,
  };
};
